import "moment-timezone";
import moment from "moment";
import { Line } from "@nivo/line";
import { Classes, Spinner, Tag } from "@blueprintjs/core";
import React from "react";
import { floatVF, integerVF, moneyVF } from "../components/AgTable/gridOptions";
import _ from "lodash";
import { useAppModeEndpoint } from "../use-app-mode-endpoint";
import { useAppMode } from "../store/model-misc";

export function NivoTimeSeriesLine({ endpoint, height, width, ...restProps }) {
  const appMode = useAppMode();
  const { data, loading } = useAppModeEndpoint(endpoint, appMode);

  return loading ? (
    <Spinner />
  ) : (
    <NivoTimeSeriesLineInner
      height={height}
      width={width}
      {...data}
      {...restProps}
    />
  );
}

function NivoTimeSeriesLineInner({
  height,
  width,
  //
  data,
  yType,
  yMin,
  yMax,
  yTicks,
  yTitle,
  xTicks,
  xTitle,
}) {
  const yFormatter = succinctFormatterFactory(yType);
  if (_.isArray(xTicks)) {
    xTicks = xTicks.map((d) => moment(d).add(8, "hours").toDate());
  }
  return (
    <Line
      data={data}
      theme={NIVO_LINE_THEME}
      lineWidth={2}
      height={height}
      width={width}
      margin={{ top: 20, right: 12, bottom: 59, left: 94 }}
      animate={true}
      xScale={{
        type: "time",
        format: "%Y-%m-%d",
        precision: "day",
        useUTC: false,
      }}
      xFormat="time:%m/%d"
      yScale={{
        type: "linear",
        stacked: false,
        min: yMin,
        max: yMax,
      }}
      axisLeft={{
        format: yFormatter,
        tickSize: 5,
        tickValues: yTicks,
        tickPadding: 5,
        legend: yTitle,
        legendOffset: -76,
        legendPosition: "middle",
      }}
      axisBottom={{
        format: "%m/%d",
        tickSize: 5,
        tickValues: "every week",
        tickPadding: 5,
        legend: xTitle,
        legendOffset: 46,
        legendPosition: "middle",
      }}
      useMesh={true}
      enableSlices={false}
      enablePoints={false}
      enableGridX={false}
      enableGridY={false}
      innerPadding={24}
      padding={24}
      tooltip={tooltipRendererFactory(yType)}
      colors={"#49aef0"}
    />
  );
}

function succinctFormatterFactory(yType) {
  return SUCCINCT_FORMATTERS_BY_TYPE[yType];
}
function formatterFactory(yType) {
  return FORMATTERS_BY_TYPE[yType];
}

// TODO: 2 sig figs + M for millions, K for thousands (will need more left margin on plot)

function succinctMoneyFormatter(value) {
  return "$" + succinctFloatFormatter(value);
}

function succinctFloatFormatter(value) {
  let suffix = "";
  if (value >= 1000000) {
    suffix = "M";
    value = value / 1000000;
  } else if (value >= 1000) {
    suffix = "K";
    value = value / 1000;
  }
  const nf = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  return nf.format(value) + suffix;
}

const SUCCINCT_FORMATTERS_BY_TYPE = {
  money: succinctMoneyFormatter,
  integer: succinctFloatFormatter,
  float: succinctFloatFormatter,
};

const FORMATTERS_BY_TYPE = {
  money: (value) => moneyVF({ value }),
  integer: (value) => integerVF({ value }),
  float: (value) => floatVF({ value }),
};

function tooltipRendererFactory(yType) {
  const yFormatter = formatterFactory(yType);
  return ({
    point: {
      data: { x, y },
    },
  }) => (
    <Tag
      large
      className={`${Classes.ELEVATION_1}`}
      css={`
        color: #fff !important;
        background-color: rgba(0, 25, 43, 0.8) !important;
      `}
    >
      {`${yFormatter(y)} on ${moment(x).format("M/D")}`}
    </Tag>
  );
}

const NIVO_LINE_THEME = {
  background: "transparent",
  fontFamily: "sans-serif",
  fontSize: 14,
  textColor: "#fff",
  axis: {
    domain: {
      line: {
        stroke: "transparent",
        strokeWidth: 1,
      },
    },
    ticks: {
      line: {
        stroke: "#b9b9b9",
        strokeWidth: 1,
      },
      text: {
        fontSize: 14,
      },
    },
    legend: {
      text: {
        fontSize: 16,
      },
    },
  },
  grid: {
    line: {
      stroke: "#dddddd",
      strokeWidth: 1,
    },
  },
  legends: {
    text: {
      fill: "#b9b9b9",
    },
  },
  labels: {
    text: {},
  },
  markers: {
    lineColor: "#000000",
    lineStrokeWidth: 1,
    text: {},
  },
  dots: {
    text: {},
  },
  tooltip: {
    container: {
      background: "rgba(26,38,51,0.8)",
      color: "inherit",
      fontSize: "inherit",
      borderRadius: "2px",
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.25)",
      padding: "5px 9px",
    },
    basic: {
      whiteSpace: "pre",
      display: "flex",
      alignItems: "center",
    },
    table: {},
    tableCell: {
      padding: "3px 5px",
    },
  },
  crosshair: {
    line: {
      stroke: "#e1e1e1",
      strokeWidth: 1,
      strokeOpacity: 0.75,
      strokeDasharray: "6 6",
    },
  },
  annotations: {
    text: {
      fontSize: 14,
      outlineWidth: 2,
      outlineColor: "#000000",
    },
    link: {
      stroke: "#000000",
      strokeWidth: 1,
      outlineWidth: 2,
      outlineColor: "#ffffff",
    },
    outline: {
      fill: "none",
      stroke: "#000000",
      strokeWidth: 2,
      outlineWidth: 2,
      outlineColor: "#ffffff",
    },
    symbol: {
      fill: "#000000",
      outlineWidth: 2,
      outlineColor: "#ffffff",
    },
  },
};
